import { useCallback, useEffect, useState, useRef } from 'react'
import axios from 'lib/axios'
import {
  ConfiguredCPI,
  DevicesDataItem,
  ModelManifest,
  DevicesQueryPagination,
  DevicesQueryResponse,
  DevicesQueryPayload,
  ColumnValue,
  IntegrationInstance,
  MetricManifest
} from 'models'
import { useAppDispatch } from 'store/hooks'
import { setDevicesCount } from 'store/trends/trendsSlice'

interface Props {
  thisCpiName: string
  cpiConfiguration?: ConfiguredCPI | null
  dataSourceOptions: IntegrationInstance[]
}

const useDevicesData = ({ thisCpiName, cpiConfiguration, dataSourceOptions }: Props) => {
  const dispatch = useAppDispatch()
  const [modelManifests, setModelManifests] = useState<ModelManifest[]>([])
  const [snowflakeManifest, setSnowflakeManifest] = useState<ModelManifest | null>(null)
  const [data, setData] = useState<DevicesDataItem[]>([])
  const [pagination, setPagination] = useState<DevicesQueryPagination>({
    current_page: 1,
    next_page: null,
    previous_page: null,
    total_results: 0
  })
  const [initialQueryLoaded, setInitialQueryLoaded] = useState(false)
  const [columnValues, setColumnValues] = useState<ColumnValue[]>([])
  const [columnsFetched, setColumnsFetched] = useState(false)
  const [manifestFetched, setManifestFetched] = useState(false)
  const [metricManifest, setMetricManifest] = useState<MetricManifest | undefined>(undefined)

  // Reset state when CPI changes - but keep the count!
  const previousCpiName = useRef(thisCpiName)

  useEffect(() => {
    // Only reset if CPI actually changed, not on every mount
    if (previousCpiName.current !== thisCpiName) {
      setColumnValues([])
      setColumnsFetched(false)
      setInitialQueryLoaded(false)
      setManifestFetched(false)
      setSnowflakeManifest(null)
      setData([])
      setPagination({
        current_page: 1,
        next_page: null,
        previous_page: null,
        total_results: 0
      })
      // Only reset count when CPI actually changes
      dispatch(setDevicesCount(0))
      previousCpiName.current = thisCpiName
    }
  }, [thisCpiName, dispatch])

  const fetchManifest = async () => {
    /* Metric/CPI Manifest */
    try {
      const metricManifestRes = await axios.post<MetricManifest[]>('/api/v4/manifests/metrics/filter?metric-manifest', {
        manifest: {
          slug: {
            any_: [cpiConfiguration?.name ?? thisCpiName]
          }
        }
      })
      if (metricManifestRes.status === 200) {
        setMetricManifest(metricManifestRes.data[0] ?? undefined)
      }
    } catch (err) {
      console.error(err)
    }

    /* Models Manifests */
    try {
      const res = await axios.post('/api/v4/manifests/models/filter?models-manifest', {
        manifest: {
          operator: 'and_'
        },
        sort: 'CREATED_DESC'
      })

      if (res.status === 200) {
        setModelManifests(res.data as ModelManifest[])
      }
    } catch (err) {
      console.error(err)
    } finally {
      setManifestFetched(true)
    }
  }

  const fetchQuery = useCallback(
    async ({ page = 1, payload }: { page?: number; payload?: DevicesQueryPayload }) => {
      if (!cpiConfiguration) return { success: false, error: 'Invalid CPI name' }

      try {
        const res = await axios.post<DevicesQueryResponse>(`/api/v4/metrics/${cpiConfiguration.name}/records`, {
          query_filter: { ...payload },
          page,
          limit: 100
        })

        if (res.status === 200) {
          const { data } = res
          setPagination(data.pagination)
          setData(data.data)
          return { success: true, response: res }
        }

        return { success: false, error: `API returned status ${res.status}` }
      } catch (err) {
        console.error('fetchQuery error:', err)
        return { success: false, error: err }
      }
    },
    [cpiConfiguration]
  )

  const getLookup = useCallback(
    async (columnId: string): Promise<string[]> => {
      if (!cpiConfiguration) return []

      try {
        const res = await axios.post(`/api/v4/metrics/${cpiConfiguration.name}/lookup`, {
          lookup: {
            column_name: columnId
          }
        })

        if (res.status === 200) {
          return res.data
        }
      } catch (err) {
        console.error(err)
      }

      return []
    },
    [cpiConfiguration]
  )

  const findManifest = useCallback(() => {
    if (cpiConfiguration && modelManifests.length > 0) {
      return modelManifests.find((model) => model.supported_metrics.includes(cpiConfiguration.name))
    }
  }, [modelManifests, cpiConfiguration])

  useEffect(() => {
    if (thisCpiName && cpiConfiguration && cpiConfiguration.is_v2) {
      fetchManifest().catch((e) => e)
    }
  }, [thisCpiName, cpiConfiguration])

  useEffect(() => {
    if (dataSourceOptions.length > 0 && manifestFetched && cpiConfiguration?.is_v2) {
      const thisModel = findManifest()

      if (thisModel) {
        // Only set up for initial load without fetching column values
        if (!initialQueryLoaded) {
          setSnowflakeManifest(thisModel)

          // Fetch first page of data to get the count for header
          const fetchInitialData = async () => {
            try {
              const result = await fetchQuery({})
              if (result.success) {
                const { pagination } = (result as any).response.data
                dispatch(setDevicesCount(pagination.total_results))
              } else {
                console.error('Initial fetch failed:', result.error)
                dispatch(setDevicesCount(0))
              }
            } catch (err) {
              console.error('Error in fetchInitialData:', err)
              dispatch(setDevicesCount(0))
            } finally {
              setInitialQueryLoaded(true)
              setColumnsFetched(true)
            }
          }

          fetchInitialData()
        }
      }
    }
  }, [dataSourceOptions, manifestFetched, findManifest, cpiConfiguration, fetchQuery, dispatch, initialQueryLoaded])

  return {
    devicesData: data,
    snowflakeManifest,
    fetchQuery,
    devicesPagination: pagination,
    initialQueryLoaded,
    getLookup,
    columnValues,
    setColumnValues,
    columnsFetched,
    setColumnsFetched,
    setInitialQueryLoaded,
    metricManifest
  }
}

export default useDevicesData
